/**
 * MSAL (Microsoft Entra ID / Office 365) wiring — entirely config-driven.
 *
 * Build-time env (set on the Railway service so they bake into the bundle):
 *   VITE_MSAL_CLIENT_ID   Entra app registration (application) id
 *   VITE_MSAL_TENANT_ID   directory (tenant) id — restricts sign-in to the org
 *   VITE_AUTH_DEV_BYPASS  '1' = pretend signed in, send x-dev-user headers
 *                         (local dev/tests only, pairs with AUTH_DEV_BYPASS on
 *                         the server; never set in deployed environments)
 *
 * With neither configured, `authEnabled` is false and the app renders exactly
 * as before this feature existed — no gate, no cloud features.
 */

import {
  PublicClientApplication,
  EventType,
  InteractionType,
  InteractionRequiredAuthError,
  type AccountInfo,
  type EventMessage,
  type RedirectRequest,
} from '@azure/msal-browser';
import { writeStamp, clearStamp } from './session';

export const msalClientId: string = import.meta.env.VITE_MSAL_CLIENT_ID ?? '';
export const msalTenantId: string = import.meta.env.VITE_MSAL_TENANT_ID ?? '';
export const authDevBypass: boolean = import.meta.env.VITE_AUTH_DEV_BYPASS === '1';

export const msalConfigured = msalClientId !== '' && msalTenantId !== '';
export const authEnabled = msalConfigured || authDevBypass;

/** Scope of our own API — the server validates tokens minted for it. */
export const apiScopes = [`api://${msalClientId}/access_as_user`];

let instance: PublicClientApplication | null = null;
let initPromise: Promise<void> | null = null;

/**
 * The account an MSAL success event is about. LOGIN_SUCCESS carries the
 * AccountInfo itself; ACQUIRE_TOKEN_SUCCESS carries the AuthenticationResult
 * that holds it.
 */
function accountFromEvent(payload: unknown): AccountInfo | null {
  if (!payload || typeof payload !== 'object') return null;
  if ('homeAccountId' in payload) return payload as AccountInfo;
  const nested = (payload as { account?: AccountInfo | null }).account;
  return nested ?? null;
}

/**
 * A token that came back through the Microsoft sign-in page (redirect or
 * popup) IS an online validation — Microsoft just vouched for the account.
 * Stamping it here, when the response is processed, is what lets the gate
 * admit the user the moment the redirect completes instead of firing a
 * second round trip against the token cache that is still being written.
 */
function onMsalEvent(event: EventMessage): void {
  const interactive =
    event.interactionType === InteractionType.Redirect ||
    event.interactionType === InteractionType.Popup;
  if (!interactive) return;
  if (
    event.eventType !== EventType.LOGIN_SUCCESS &&
    event.eventType !== EventType.ACQUIRE_TOKEN_SUCCESS
  ) {
    return;
  }
  const account = accountFromEvent(event.payload);
  if (!account || !instance) return;
  instance.setActiveAccount(account);
  writeStamp(account.homeAccountId);
}

export function getMsalInstance(): PublicClientApplication {
  if (!instance) {
    instance = new PublicClientApplication({
      auth: {
        clientId: msalClientId,
        authority: `https://login.microsoftonline.com/${msalTenantId}`,
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
      },
      cache: { cacheLocation: 'localStorage' },
    });
    instance.addEventCallback(onMsalEvent);
  }
  return instance;
}

/** Await before rendering MsalProvider (MSAL v3+ requires initialize()). */
export function ensureMsalInitialized(): Promise<void> {
  if (!initPromise) initPromise = getMsalInstance().initialize();
  return initPromise;
}

export type SessionCheck = 'valid' | 'interaction' | 'offline';

/** MSAL error codes that genuinely mean "couldn't reach Microsoft". */
const NETWORK_ERROR_CODES = new Set([
  'network_error',
  'no_network_connectivity',
  'post_request_failed',
  'endpoints_resolution_error',
]);

function errorCode(err: unknown): string {
  return (err as { errorCode?: string } | null)?.errorCode ?? '';
}

/**
 * Online session check: forces a refresh-token round trip to the Microsoft
 * token endpoint.
 *
 *  - 'valid'       token renewed; stamp written.
 *  - 'interaction' silent renewal can't succeed but an interactive round
 *    trip would settle it — this covers BOTH the routine case (SPA refresh
 *    tokens expire after 24h and the hidden-iframe fallback fails under
 *    third-party-cookie blocking → the redirect completes instantly against
 *    the live Entra session) AND the revoked case (Microsoft refuses the
 *    interactive sign-in at its own door). NEVER map this to a local
 *    lockout screen — a Retry button cannot fix it.
 *  - 'offline'     network unreachable; the 48h grace window applies.
 */
export async function checkSessionOnline(): Promise<SessionCheck> {
  const inst = getMsalInstance();
  const account = inst.getActiveAccount() ?? inst.getAllAccounts()[0];
  if (!account) return 'interaction';
  try {
    await inst.acquireTokenSilent({ scopes: apiScopes, account, forceRefresh: true });
    writeStamp(account.homeAccountId);
    return 'valid';
  } catch (err) {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';
    if (err instanceof InteractionRequiredAuthError) return 'interaction';
    return NETWORK_ERROR_CODES.has(errorCode(err)) ? 'offline' : 'interaction';
  }
}

/**
 * MSAL marks "a redirect is mid-flight" in sessionStorage before it navigates
 * to Microsoft and clears the mark when the response comes back. Leave the
 * Microsoft page any other way — Back, a closed tab restored, a phone browser
 * that reloads the tab — and the mark outlives the redirect. Every later
 * redirect then throws `interaction_in_progress` without navigating, which
 * is a sign-in button that does nothing and a verification screen that never
 * ends. Removing the stale mark is the documented recovery.
 *
 * Returns true when a mark was found and removed.
 */
export function clearStaleInteraction(): boolean {
  try {
    const stale: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.endsWith('.interaction.status')) stale.push(key);
    }
    for (const key of stale) sessionStorage.removeItem(key);
    return stale.length > 0;
  } catch {
    return false;
  }
}

/**
 * Start an interactive Microsoft round trip. On success the page navigates
 * away and the promise never resolves. A stale in-progress mark is cleared
 * and the redirect retried once; anything else is thrown to the caller.
 */
async function redirectInteractive(request: RedirectRequest): Promise<void> {
  const inst = getMsalInstance();
  try {
    await inst.acquireTokenRedirect(request);
  } catch (err) {
    if (errorCode(err) === 'interaction_in_progress' && clearStaleInteraction()) {
      await inst.acquireTokenRedirect(request);
      return;
    }
    throw err;
  }
}

/** Interactive re-auth for the cached account (navigates away). */
export async function redirectForInteraction(): Promise<void> {
  const inst = getMsalInstance();
  const account = inst.getActiveAccount() ?? inst.getAllAccounts()[0];
  await redirectInteractive({ scopes: apiScopes, account: account ?? undefined });
}

/** First sign-in from the gate (navigates away). */
export async function loginInteractive(): Promise<void> {
  await redirectInteractive({ scopes: apiScopes });
}

/** Wipe the cached session (revoked account) so the gate shows sign-in. */
export async function purgeSession(): Promise<void> {
  clearStamp();
  clearStaleInteraction();
  await getMsalInstance().clearCache();
}

/** Full reload so the gate re-evaluates against the now-empty cache. */
export function reloadApp(): void {
  window.location.reload();
}

/**
 * Access token for our API. Silent first; falls back to a redirect (the
 * promise then never resolves — the page navigates to the login flow).
 */
export async function getApiToken(): Promise<string> {
  const inst = getMsalInstance();
  const account = inst.getActiveAccount() ?? inst.getAllAccounts()[0];
  if (!account) throw new Error('not-signed-in');
  try {
    const result = await inst.acquireTokenSilent({ scopes: apiScopes, account });
    return result.accessToken;
  } catch {
    await redirectInteractive({ scopes: apiScopes, account });
    throw new Error('redirecting-for-token');
  }
}
